import React, { FunctionComponent, useEffect, useMemo, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import { TrialPeriodCountdownBanner } from '@/ui/components';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import * as accountActions from '@payaca/store/account/accountActions';
import { actions as usersActions } from '../../../api/users';
import TrialPeriodExpiryBanner from '../trialPeriodExpiryBanner/TrialPeriodExpiryBanner';
import TrialSubscriptionUpgradeBanner from '../trialSubscriptionUpgradeBanner/TrialSubscriptionUpgradeBanner';
import UserSeatsExceededBanner from '../userSeatsExceededBanner/UserSeatsExceededBanner';

const AccountAccessBanner: FunctionComponent = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { userHasRequiredPermission } = usePermissions();

  const accountAccessInformation = useSelector((state) => {
    return state.account.accountAccessInformation;
  });

  const {
    shouldRenderTrialPeriodCountdownBanner = false,
    shouldRenderTrialPeriodExpiryBanner = false,
    shouldRenderUserSeatsExceededBanner = false,
    shouldRenderTrialSubscriptionUpgradeBanner = false,
  } = useMemo(() => {
    if (!accountAccessInformation) {
      return {};
    }
    const {
      hasActiveSubscription,
      isTrialPeriodExpired,
      trialPeriodDaysRemaining,
    } = accountAccessInformation;
    return {
      shouldRenderTrialPeriodCountdownBanner:
        !isTrialPeriodExpired &&
        !hasActiveSubscription &&
        trialPeriodDaysRemaining !== null,
      shouldRenderTrialPeriodExpiryBanner:
        isTrialPeriodExpired &&
        !hasActiveSubscription &&
        trialPeriodDaysRemaining !== null,
      shouldRenderUserSeatsExceededBanner:
        hasActiveSubscription &&
        accountAccessInformation.hasExceededActiveSubscriptionUserSeats,
      shouldRenderTrialSubscriptionUpgradeBanner:
        hasActiveSubscription && accountAccessInformation.temporarySubscription,
    };
  }, [accountAccessInformation]);

  useEffect(() => {
    dispatch(accountActions.requestGetAccountAccessInformation());
    if (userHasRequiredPermission([AccountsPermissions.GET_USERS])) {
      dispatch(usersActions.getAccountUsers());
    }
  }, [dispatch, userHasRequiredPermission]);

  return (
    <React.Fragment>
      {shouldRenderTrialPeriodCountdownBanner && <TrialPeriodCountdownBanner />}
      {shouldRenderUserSeatsExceededBanner && (
        <PermissionGuard
          renderIfHasPermissions={[AccountsPermissions.UPDATE_SUBSCRIPTION]}
        >
          <UserSeatsExceededBanner />
        </PermissionGuard>
      )}
      {shouldRenderTrialPeriodExpiryBanner && <TrialPeriodExpiryBanner />}
      {shouldRenderTrialSubscriptionUpgradeBanner && (
        <TrialSubscriptionUpgradeBanner />
      )}
    </React.Fragment>
  );
};

export default AccountAccessBanner;
